import VueWrapper from '@/components/core/Vue/vue.wrapper';
import {KeyValuePair, PaymentMethodsList} from '@/globals';
import {SignupInvitationModel, SignupViewModel} from '@/sdk';
import {AutentictionApi} from '@/sdk/api-services';
import {SignupInvitationAppi} from '@/sdk/api-services/signup-invitation.api';
import {Component} from 'vue-property-decorator';

@Component
export default class SignupComponent extends VueWrapper {
    public orgCode: String | null = null;
    public loginId: String | null = null;
    public password: String | null = null;

    public consultationDialogName: string = 'request-consultation-dialog';

    public show1: boolean = false;

    public signupInvitationApi: SignupInvitationAppi = new SignupInvitationAppi();

    public periods: Array<KeyValuePair> = [
        {
            key: 'Annual',
            value: 'Annual',
        },
        {
            key: 'Monthly',
            value: 'Monthly',
        },
        {
            key: 'Customize',
            value: 'Customize',
        },
    ];

    public paymentMethods = PaymentMethodsList;
    public authenticationApi: AutentictionApi = new AutentictionApi();

    public isCheckingInvitation: boolean = false;

    public invitation: SignupInvitationModel | null = null;

    public signupData: SignupViewModel = new SignupViewModel();

    public smsOptInConsent: boolean = false;

    public signupMode: 'invitation' | 'own' = 'own';

    public created() {
        this.signupData.invitationCode = this.$route.query.code as string;
        if (this.signupData.invitationCode) {
            this.signupMode = 'invitation';
            this.checkInvitationId();
        }
    }

    public mounted() {
        //
    }

    public localeChange() {
        //
    }

    public async signup() {
        try {
            if (!this.smsOptInConsent) {
                this.AlertSrv.show('error', 'Please provide SMS consent to continue.');
                return;
            }

            const smsConsentPayload = {
                smsOptInConsent: this.smsOptInConsent,
                smsOptInAcceptedAt: new Date().toISOString(),
                smsOptInPolicyVersion: '2026-02-15',
            };

            if (this.isInvitationMode && !this.isValidInvitation) {
                this.AlertSrv.show('error', 'Please verify a valid invitation code first.');
                return;
            }

            if (!this.isInvitationMode) {
                const res = await this.authenticationApi.SignupAdmin({...this.signupData, ...smsConsentPayload, invitationCode: null});
                this.AlertSrv.show('success', 'You have been signed up successfully!').then(() => {
                    this.$router.push({
                        name: 'phoneVerification',
                        query: {
                            userId: res.user.id,
                            phone: this.signupData.mobilePhone,
                            loginId: this.signupData.email,
                        },
                    });
                });
                return;
            }

            if (this.invitation?.type === 'AdminInvitation') {
                const res = await this.authenticationApi.SignupAdmin({...this.signupData, ...smsConsentPayload});
                this.AlertSrv.show('success', 'You have been signed up successfully!').then(() => {
                    this.$router.push({
                        name: 'phoneVerification',
                        query: {
                            userId: res.user.id,
                            phone: this.signupData.mobilePhone,
                            loginId: this.signupData.email,
                        },
                    });
                });
            } else if (this.invitation?.type === 'UserInvitation') {
                const res = await this.authenticationApi.SignupUser({...this.signupData, ...smsConsentPayload, accountId: this.invitation.accountId});
                this.AlertSrv.show('success', 'You have been signed up successfully!').then(() => {
                    this.$router.push({
                        name: 'phoneVerification',
                        query: {
                            userId: res.id,
                            phone: this.signupData.mobilePhone,
                            loginId: this.signupData.email,
                        },
                    });
                });
            }
        } catch (err: any) {
            this.AlertSrv.show('error', err?.error?.message ?? 'Unable to signup. Please try again.');
        }
    }

    public get isValidInvitation() {
        return this.invitation && this.invitation.expiresAt && new Date(this.invitation.expiresAt) > new Date();
    }

    public get isInvitationMode() {
        return this.signupMode === 'invitation';
    }

    public get isOwnAccount() {
        return this.signupMode === 'own';
    }

    public get canEditSignupDetails() {
        return this.isOwnAccount || this.isValidInvitation;
    }

    public get termsPolicyUrl() {
        const locale = ((this as any).$i18n?.locale ?? 'en') as string;
        return locale.toLowerCase().startsWith('ko') ? '/policies/termsAndConditionsKr.html' : '/policies/termsAndConditionsEn.html';
    }

    public get privacyPolicyUrl() {
        const locale = ((this as any).$i18n?.locale ?? 'en') as string;
        return locale.toLowerCase().startsWith('ko') ? '/policies/privacyPolicyKr.html' : '/policies/privacyPolicyEn.html';
    }

    public handleSignupModeChange() {
        if (this.signupMode === 'own') {
            this.invitation = null;
            this.signupData.invitationCode = null;
            this.signupData.accountId = null;
        }
    }

    public checkInvitationId() {
        this.LoaderSrv.showFullScreenLoader('Checking Invitation...');

        this.isCheckingInvitation = true;
        this.signupInvitationApi
            .checkInvitationByCode(this.signupData.invitationCode!)
            .then(res => {
                this.invitation = res;

                if (!res || (res.expiresAt && new Date(res.expiresAt) < new Date())) {
                    this.AlertSrv.show('error', 'Your invitateion has been expired.');
                } else {
                    this.signupData.email = res.email;
                }
            })
            .catch(err => {
                this.AlertSrv.show('error', 'Invalid or expired invittion.');
                this.invitation = null;
            })
            .finally(() => {
                this.isCheckingInvitation = false;
                this.LoaderSrv.hideFullScreenLoader();
            });
    }

    public openConsultationDialog() {
        this.CoreSrv.OpenModal(this.consultationDialogName);
    }

    public closeConsultationDialog() {
        this.CoreSrv.CloseModal(this.consultationDialogName);
    }
}
